import { useChatContext } from '@/chat/ChatContext';
import { productIdFromSlug } from '@/utils/slug';
import { openInNewTab } from '@/utils/url-template';

export interface ProductChipProps {
  slug: string;
  /** the name the reply wrote for it; else the product's title */
  label?: string;
}

/**
 * Renders an inline product reference inside running prose. Reads as a
 * subtle accented link (serif italic + dotted underline) — NOT a boxed
 * highlight, which competes with paragraph rhythm.
 */
export function ProductChip({ slug, label }: ProductChipProps): JSX.Element | null {
  const { payload, onProductDetailsClick, productPageUrl, onPreviewProduct } = useChatContext();
  const product = payload.products.find((p) => p.slug === slug);

  // 找不到产品对象时绝不能返回 null——chip 嵌在句子中间，返回 null 会留下
  // 「La  est une formule…」这样的空洞。退化成不可点击的纯文本产品名。
  if (!product) {
    return <span className="tw-font-semibold tw-text-cal-green-700">{label ?? readableFromSlug(slug)}</span>;
  }
  const resolvedProduct = product;

  function handleClick() {
    if (onPreviewProduct) {
      onPreviewProduct(slug);
      return;
    }
    const id = resolvedProduct.id ?? productIdFromSlug(slug) ?? '';
    if (onProductDetailsClick) {
      onProductDetailsClick(id, slug);
    } else if (productPageUrl) {
      openInNewTab(productPageUrl.replace('{slug}', encodeURIComponent(slug)));
    } else if (resolvedProduct.url) {
      openInNewTab(resolvedProduct.url);
    }
  }

  // A <button> always lays out as one unbreakable box, so a long name jumped to the
  // next line and left "La" stranded. An inline span wraps with the sentence;
  // role, tabIndex and Enter/Space keep it a button.
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={handleClick}
      content-content="chatbot-texte-produit"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className="gtmtrack tw-cursor-pointer tw-text-cal-green-700 tw-font-semibold tw-underline tw-decoration-cal-green-200 tw-decoration-2 tw-underline-offset-2 hover:tw-decoration-cal-green-500 hover:tw-text-cal-green-900 tw-transition-colors"
    >
      {label ?? resolvedProduct.title}
    </span>
  );
}

/**
 * 从 slug 反推一个可读的产品名：去掉尾部数字 id，连字符换空格。
 * `chai-hu-an-mian-wan-formule-sommeil-4255` → `chai hu an mian wan formule sommeil`
 */
function readableFromSlug(slug: string): string {
  return slug.replace(/-\d{3,}$/, '').replace(/-/g, ' ');
}
